import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { NumField } from "./NumField";

const MARGINS = [10, 15, 20, 25];
const OPTIONAL = [["tolls", "Tolls"], ["permits", "Permits"], ["hotel", "Hotel"], ["meals", "Meals"], ["parking", "Parking"], ["other", "Other"]];

const Section = ({ n, title, children }) => (
  <section className="card">
    <div className="card-head"><span className="card-n">{n}</span><h2>{title}</h2></div>
    {children}
  </section>
);

const Fld = ({ f, set, id, label, ...extra }) => <NumField id={id} label={label} value={f[id]} onChange={(v) => set(id, v)} {...extra} />;

const LoadSection = (p) => (
  <Section n="01" title="Load & Miles">
    <div className="grid2">
      <div className="span2"><Fld {...p} id="offer" label="Customer Offer" prefix="$" placeholder="2850" /></div>
      <Fld {...p} id="loaded_miles" label="Loaded Miles" suffix="mi" placeholder="850" />
      <Fld {...p} id="deadhead_miles" label="Deadhead Miles" suffix="mi" placeholder="65" />
      <div className="span2"><Fld {...p} id="return_miles" label="Return / Unpaid Miles" suffix="mi" placeholder="420" /></div>
    </div>
  </Section>
);

const FuelSection = (p) => (
  <Section n="02" title="Fuel & Truck">
    <div className="grid2">
      <Fld {...p} id="fuel_price" label="Fuel Price" prefix="$" suffix="/gal" placeholder="3.89" />
      <Fld {...p} id="maintenance_cpm" label="Maintenance CPM" prefix="$" suffix="/mi" placeholder="0.18" />
      <Fld {...p} id="loaded_mpg" label="Loaded MPG" placeholder="8.5" />
      <Fld {...p} id="empty_mpg" label="Empty MPG" placeholder="12" />
    </div>
  </Section>
);

const DriverSection = (p) => (
  <Section n="03" title="Driver Pay">
    <div className="seg" role="tablist" data-testid="toggle-driver-pay-mode">
      {[["flat", "Flat / Trip"], ["hourly", "Hourly"]].map(([m, l]) => (
        <button key={m} type="button" data-testid={`driver-mode-${m}`} className={p.f.driver_mode === m ? "on" : ""} onClick={() => p.set("driver_mode", m)}>{l}</button>
      ))}
    </div>
    {p.f.driver_mode === "flat" ? (
      <Fld {...p} id="driver_flat" label="Flat Pay per Trip" prefix="$" placeholder="600" />
    ) : (
      <div className="grid2">
        <Fld {...p} id="driver_rate" label="Hourly Rate" prefix="$" suffix="/hr" placeholder="32" />
        <Fld {...p} id="driver_hours" label="Hours" suffix="hr" placeholder="24" />
      </div>
    )}
  </Section>
);

const MarginSection = ({ marginInvalid, ...p }) => (
  <Section n="04" title="Desired Margin">
    <div className="chips" data-testid="margin-chips">
      {MARGINS.map((m) => (
        <button key={m} type="button" data-testid={`chip-margin-${m}`} className={`chip ${String(p.f.margin_choice) === String(m) ? "on" : ""}`} onClick={() => p.set("margin_choice", m)}>{m}%</button>
      ))}
      <button type="button" data-testid="chip-margin-custom" className={`chip ${p.f.margin_choice === "custom" ? "on" : ""}`} onClick={() => p.set("margin_choice", "custom")}>Custom</button>
    </div>
    {p.f.margin_choice === "custom" && (
      <div className="custom-margin">
        <Fld {...p} id="custom_margin" label="Custom Margin (1–99)" suffix="%" placeholder="30" />
        {marginInvalid && <p className="err" data-testid="custom-margin-error">Enter a margin between 1 and 99%</p>}
      </div>
    )}
  </Section>
);

const OptionalSection = (p) => {
  const [open, setOpen] = useState(false);
  const total = OPTIONAL.reduce((s, [k]) => s + (parseFloat(p.f[k]) || 0), 0);
  return (
    <section className="card">
      <button type="button" className="collapse-head" data-testid="toggle-optional-expenses" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="card-n">05</span>
        <h2>Optional Expenses</h2>
        <span className="collapse-total" data-testid="optional-total">{total > 0 ? `$${total.toFixed(2)}` : "None"}</span>
        <ChevronDown size={18} className={`chev ${open ? "open" : ""}`} />
      </button>
      {open && (
        <div className="grid3 collapse-body" data-testid="optional-expenses-body">
          {OPTIONAL.map(([k, l]) => <div key={k}><Fld {...p} id={k} label={l} prefix="$" placeholder="0" /></div>)}
        </div>
      )}
    </section>
  );
};

export const CalcForm = ({ f, set, marginInvalid }) => (
  <div className="form" data-testid="calculator-form">
    <LoadSection f={f} set={set} />
    <FuelSection f={f} set={set} />
    <DriverSection f={f} set={set} />
    <MarginSection f={f} set={set} marginInvalid={marginInvalid} />
    <OptionalSection f={f} set={set} />
  </div>
);
